/*
 * Copyright (c) GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import {QueryList} from '@angular/core';
import {FabFilterDescriptor} from "@fabric/components/table-filter/data-query";
import {ColDef} from 'ag-grid-community';
import {FCORG_BLACKLIST} from '../../common/constants';
import {IndustryScenario} from "../../models/industry-scenario-dto.model";
import {VehicleScenario} from "../../models/vehicle-scenario-dto.model";
import {ColumnHeader} from '../models/column-header.model';

/**
 * Return an array with the range of numbers between the start and end, noninclusive of the end number
 * @param start the start number
 * @param end the end number
 */
export function range(start, end): number[] {
  if (start === end) {
    return [start];
  }
  return Array.from({length: (end - start)}, (v, k) => k + start);
}

/**
 * Function used to get the value for a specific column
 * @param obj - the object to get data from
 * @param path - the path to the data from the object
 */
export function getProperty(obj, path): string {
  return path.split('.').reduce((o, p) => o && o[p], obj);
}

/**
 * Function used to check whether an object is empty
 * @param obj the object
 */
export function isEmpty(obj) {
  return Object.keys(obj).length === 0;
}

/**
 * Sorts a list of fcorgs and removes any blacklisted fcorgs
 * @param fcorgs The list of fcorgs to sort
 * @param filterUnusedFcorgs Boolean to remove unused FCORGS
 */
export function sortFcorgs(fcorgs: string[], filterUnusedFcorgs: boolean = true) {
  const sortedFcorgs = fcorgs.sort((a, b) => {
    if (a !== null && a !== undefined && b !== null && b !== undefined) {
      return a.localeCompare(b)
    } else {
      return 0;
    }
  })
  if (filterUnusedFcorgs) {
    return sortedFcorgs.filter(fcorg => !FCORG_BLACKLIST.includes(fcorg));
  } else {
    return sortedFcorgs;
  }
}

/**
 * Get the default year range for the year picker
 */
export function getDefaultYearRange(): {yearRange: number[], startYear: number, endYear: number} {
  const currentYear = new Date().getFullYear();
  const startYear = currentYear - 13;
  const endYear = currentYear + 26;
  return {yearRange: range(startYear, endYear), startYear: currentYear - 2, endYear: currentYear + 9};
}

/**
 * Sorts a list of scenarios by Fcorg then id descending
 * @param vehicleScenarios The list of scenarios
 */
export function sortVehicleScenarios(vehicleScenarios: VehicleScenario[]): VehicleScenario[] {

  return vehicleScenarios.sort((a, b) => {
    // If fcorg values don't exist, return 0
    if (a.fcorg === null || a.fcorg === undefined || b.fcorg === null || b.fcorg === undefined) {
      return 0;
    }

      // Sort by fcorg
    const nameComparison = a.fcorg.localeCompare(b.fcorg);
    if (nameComparison !== 0) {
      return nameComparison;
    }

    // If fcorgs are equal, sort by id
    return b.vehScenId - a.vehScenId;
  });
}

/**
 * Sorts a list of scenarios by Fcorg then id descending
 * @param industryScenarios The list of Industry scenarios
 */
export function sortIndustryScenarios(industryScenarios: IndustryScenario[]): IndustryScenario[] {

  return industryScenarios.sort((a, b) => {
    // If fcorg values don't exist, return 0
    if (a.forecastOrganization === null || a.forecastOrganization === undefined || b.forecastOrganization === null || b.forecastOrganization === undefined) {
      return 0;
    }

    // Sort by fcorg
    const nameComparison = a.forecastOrganization.localeCompare(b.forecastOrganization);
    if (nameComparison !== 0) {
      return nameComparison;
    }

    // If fcorgs are equal, sort by id
    return b.id - a.id;
  });
}

/**
 * Returns the data after the active filters have been applied to it
 * @param data the data to filter
 * @param activeFilters the active filters to apply
 * @return The filtered data
 */
export function filterData(data: any[], activeFilters: FabFilterDescriptor[]): any[] {
  let filteredData = [...data];
  // If there are active filters, then filter the data
  if (activeFilters.length > 0) {
    activeFilters.forEach(filter => {
      filteredData = filteredData.filter(row => {
        const property = getProperty(row, filter.field);
        if (property !== null && property !== undefined) {
          return property.toString().toLowerCase().includes(filter.value.toLowerCase());
        } else {
          // if property is null, treat it as an empty string
          return ''.includes(filter.value.toLowerCase());
        }
      });
    });
  }
  return filteredData;
}

/**
 * Checks if an array has duplicates
 * @param array the array to check
 */
export function hasDuplicates(array): boolean {
  return (new Set(array)).size !== array.length;
}

/**
 * Function used to get the autocomplete options for a column
 * @param column the column to get the options for
 * @param data the data to get the options from
 */
export function getAutocompleteOptions(column: string, data: any): any[] {
  return [...new Set(data.map(row => {
    if (getProperty(row, column)) {
      return getProperty(row, column).toString()
    }
  }))];
}

/**
 * Function used to clear all filters for FabFilters in a QueryList
 * @param tableFilters the QueryList of FabFilters
 */
export function clearAllFilters(tableFilters: QueryList<any>): void {
  tableFilters.toArray().forEach((x) => x.clearFilter());
}

/**
 * Checks if a number is between two numbers
 * @param number The number to check
 * @param a The first number
 * @param b The second number
 * @param inclusive Whether or not to include a and b in the comparison
 */
export function between(number, a, b, inclusive) {
  const min = Math.min(a, b);
  const max = Math.max(a, b);
  return inclusive ? number >= min && number <= max : number > min && number < max;
}

/**
 * Creates the column definitions for the ag-grid table
 * @param columnHeaders the column headers to create the column definitions from
 */
export function createColDefs(columnHeaders: ColumnHeader[]): ColDef[] {
  return columnHeaders.map(header => ({
    field: header.name,
    headerName: header.label
  }));
}
